/**
 * BillSummarySection Component
 * Displays "Ringkasan Tagihan" with Total, Hemat, and Total Tagihan
 * Matches old checkout PaymentSummary exactly
 */

import { Box, Flex, Stack, useBreakpointValue } from '@chakra-ui/react';
import { Divider, Text } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { memo } from 'react';

export interface BillSummarySectionProps {
  /**
   * Order data from GET /order/meta/{id}
   * Contains original_total, saved_total, and total
   */
  orderData?: {
    original_total?: number;
    saved_total?: number;
    total?: number;
  };
}

/**
 * BillSummarySection Component
 * Displays billing summary with total, savings, and total tagihan
 *
 * Performance optimizations:
 * - React.memo prevents re-renders when cartTotalData unchanged
 */
function BillSummarySectionInner({ orderData }: BillSummarySectionProps) {
  const subtotal = orderData?.original_total || 0;
  const savedTotal = orderData?.saved_total || 0;
  const finalTotal = orderData?.total || 0;

  const hasSavings = savedTotal > 0;

  const subtotalLabel = useBreakpointValue({ base: 'Total', md: 'Sub Total' });
  const totalLabel = useBreakpointValue({ base: 'Total Tagihan', md: 'Total' });

  return (
    <Box p={5}>
      <Stack spacing={3}>
        {/* Header */}
        <Text fontSize="lg" fontWeight="bold" color="gray.800">
          Ringkasan Tagihan
        </Text>
        <Divider borderColor="#D9D9D9" borderBottomWidth="1px" />

        {/* Sub Total / Total */}
        <Flex justify="space-between" align="center">
          <Text
            fontSize={{ base: '14px', md: 'md' }}
            color={{ base: '#999999', md: 'gray.700' }}
          >
            {subtotalLabel}
          </Text>
          <Text
            fontSize={{ base: '12px', md: 'md' }}
            fontWeight={{ base: 'normal', md: 'medium' }}
            color={{ base: '#999999', md: 'gray.800' }}
            textDecoration={{
              base: hasSavings ? 'line-through' : 'none',
              md: 'none',
            }}
          >
            {buyerCurrencyFormatter(subtotal)}
          </Text>
        </Flex>

        {/* Hemat (only if there are savings) */}
        {hasSavings && (
          <Flex justify="space-between" align="center">
            <Text
              fontSize={{ base: '14px', md: 'md' }}
              color={{ base: '#999999', md: 'gray.700' }}
            >
              Hemat
            </Text>
            <Text
              fontSize={{ base: '12px', md: 'md' }}
              fontWeight={{ base: 'normal', md: 'medium' }}
              color={{ base: '#FF0000', md: 'red' }}
            >
              {buyerCurrencyFormatter(savedTotal)}
            </Text>
          </Flex>
        )}

        <Divider display={{ base: 'none', md: 'block' }} />

        {/* Total / Total Tagihan */}
        <Flex justify="space-between" align="center">
          <Text
            fontSize={{ base: '14px', md: 'lg' }}
            fontWeight={{ base: 'normal', md: 'bold' }}
            color={{ base: '#999999', md: 'gray.800' }}
          >
            {totalLabel}
          </Text>
          <Text
            fontSize={{ base: '14px', md: 'xl' }}
            fontWeight={{ base: 700, md: 'bold' }}
            color={{ base: '#131415', md: 'blue.600' }}
          >
            {buyerCurrencyFormatter(finalTotal)}
          </Text>
        </Flex>
      </Stack>
    </Box>
  );
}

// Memoize to prevent unnecessary re-renders from parent
export const BillSummarySection = memo(BillSummarySectionInner);
